import React, { FC, useState, useCallback, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useCookies } from "hooks/useCookies";
import Cookies from "universal-cookie";
import { useIsoCookies } from "hooks/useIsoCookies";
import { IconButton, Collapse } from "@mui/material";
import RHCloseIcon from "icon-close";
import { queryAboveNavPromo } from "graphql-client/queries/above-nav-promo";
import { parseUserContextCookie } from "utils/parseUserContextCookie";
import memoize from "utils/memoize";
import { useCookiesWithPermission } from "hooks/useCookiesWithPermission";
import useSite from "hooks/useSite";
import { useCountry } from "hooks/useCountry";
import useIsHomePage from "hooks/useIsHomePage";
import { processEnvServer } from "hooks/useSsrHooks";
import yn from "yn";
import { useIsCartPages } from "@RHCommerceDev/hooks/useIsCartPages";
import { useEnv } from "@RHCommerceDev/hooks/useEnv";
import { useAppId } from "hooks/useAppId";
import { useParams2 } from "@RHCommerceDev/hooks/useParams";
import useDidMountEffect from "@RHCommerceDev/hooks/useDidMountEffect";
import useUserPreferences from "@RHCommerceDev/hooks/useUserPreferences";
import { useHeaderAtom } from "@RHCommerceDev/hooks/atoms/useHeaderAtom";
import { useIsAboveNavHiddenPage } from "@RHCommerceDev/hooks/useAboveNavHiddenPage";

const PROMO_COOKIE_NAME = "PROMO";

export interface AboveNavPromoProps {}

export const AboveNavPromo: FC<AboveNavPromoProps> = memoize(() => {
  const [cookies] = useCookies([PROMO_COOKIE_NAME]);
  const { isCheckoutPages } = useIsCartPages();
  const env = useEnv();
  const { isConcierge } = useAppId();
  const hideNavOnCheckout = isCheckoutPages;
  const { state: headerState } = useHeaderAtom();
  const isSaleBannerClosed = !processEnvServer
    ? sessionStorage.getItem("SALE_BANNER_CLOSED") === "true"
    : false;

  const [pageLoaded, setPageLoaded] = useState(false);
  const [open, setOpen] = useState(false);

  const { language } = useUserPreferences();

  const siteId = useSite();
  const cookiesForCurrencyCode = new Cookies();
  const cookie = cookiesForCurrencyCode.get("userContext");
  const usrContextCookie = parseUserContextCookie(cookie);
  const country = useCountry();
  const isHomePage = useIsHomePage();
  const deviceType =
    useIsoCookies(["PF_EXP"])?.PF_EXP?.toLowerCase() || "desktop";
  const { params } = useParams2({ newPdpLayout: false });
  const newPdpLayout = yn(params?.newPdpLayout);
  const isAboveNavHiddenPage = useIsAboveNavHiddenPage();

  const {
    data: { aboveNavPromo } = {} as Query,
    loading,
    refetch
  } = useQuery<Query>(queryAboveNavPromo, {
    variables: {
      siteId,
      deviceType: deviceType,
      currencyCode: usrContextCookie?.currencyCode,
      countryCode: country
    },
    skip: hideNavOnCheckout
  });

  // Wait for page to load before showing banner
  useEffect(() => {
    const handlePageLoad = () => {
      // Use a small delay to ensure DOM is stable
      setTimeout(() => {
        setPageLoaded(true);
      }, 100);
    };

    if (document.readyState === "complete") {
      handlePageLoad();
    } else {
      window.addEventListener("load", handlePageLoad);
      return () => window.removeEventListener("load", handlePageLoad);
    }
  }, []);

  // Only show banner after page has loaded
  useEffect(() => {
    if (!pageLoaded || hideNavOnCheckout || loading) return;

    // When the nav dropdown is open, the nav banner will be suppressed.
    if (
      headerState?.isHamburgerDropdownOpen ||
      headerState?.selectedCategoryIndex !== -1
    ) {
      setOpen(false);
      return;
    }
    if (isAboveNavHiddenPage) {
      setOpen(false);
      return;
    }
    if (!isSaleBannerClosed && aboveNavPromo?.text) {
      setOpen(true);
    }
  }, [
    isAboveNavHiddenPage,
    pageLoaded,
    hideNavOnCheckout,
    loading,
    isSaleBannerClosed,
    aboveNavPromo?.text,
    headerState?.isHamburgerDropdownOpen,
    headerState?.selectedCategoryIndex
  ]);

  useDidMountEffect(() => {
    refetch();
  }, [language]);

  const { [PROMO_COOKIE_NAME]: PROMO = [] } = cookies;

  const closeBanner = useCallback(() => {
    if (processEnvServer) {
      return 0;
    }

    requestAnimationFrame(() => {
      const aboveNavBannerElem = document.querySelector(
        "#aboveNavBanner"
      ) as HTMLElement | null;
      aboveNavBannerElem?.setAttribute("style", "display: none;");

      const mainElem = document.querySelector("#main") as HTMLElement | null;
      const appNavigationBar = document.querySelector(
        "#app-navigation-bar"
      ) as HTMLElement | null;

      if (mainElem) {
        mainElem.style.paddingTop = "0";
      }
      if (isHomePage && appNavigationBar) {
        appNavigationBar.style.top = "0";
      }
    });
  }, [isHomePage]);

  const handleClose = useCallback(() => {
    setOpen(false);
    closeBanner?.();
    sessionStorage.setItem("SALE_BANNER_CLOSED", "true");
  }, [closeBanner]);

  const { setCookieWrapper } = useCookiesWithPermission();

  const handleCleanUp = useCallback(() => {
    setCookieWrapper(PROMO_COOKIE_NAME, PROMO);
  }, [setCookieWrapper, PROMO]);

  // Don't render anything during loading or before page load
  if (!pageLoaded || loading || hideNavOnCheckout) {
    return null;
  }

  if (!aboveNavPromo || !aboveNavPromo?.text) {
    closeBanner?.();
    return null;
  }

  return (
    <Collapse
      in={open}
      data-banner="above-nav"
      onExited={handleCleanUp}
      style={{
        zIndex: newPdpLayout ? 1100 : 2,
        position: "relative"
      }}
      unmountOnExit
      timeout={300} //For smooth transition
      data-nosnippet
    >
      <div
        dangerouslySetInnerHTML={{ __html: aboveNavPromo?.text || "" }}
        data-nosnippet
      />
      <IconButton
        disableRipple
        data-above-nav-close="close-nav-button"
        onClick={handleClose}
        style={{
          color: "white",
          fontSize: 16,
          position: "absolute",
          right: 7,
          top: 5
        }}
      >
        <RHCloseIcon />
      </IconButton>
    </Collapse>
  );
});

AboveNavPromo.defaultProps = {};

export default AboveNavPromo;
